import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Logger, WINDOW } from '@frontend/vanilla/core';
import { isObject, isString } from 'lodash-es';
import { filter } from 'rxjs';

import { isNavigationTraversal } from '../../view-transition-navigation.helper';
import { BROWSER_BACK_NAVIGATION_DETECTED_MESSAGE, VIEW_TRANSITION_LOG_TAG as LOG_TAG } from '../../view-transition.constants';
import { BrowserBackDetectionStrategy } from './model/browser-back-detection.strategy.model';

const TIMESTAMP_KEY = 'HistoryApiBackDetectionStrategy_firstSeenTimestamp';

type StampedState = Record<string, unknown> & {
    [TIMESTAMP_KEY]: number;
};

export class HistoryApiBackDetectionStrategy implements BrowserBackDetectionStrategy {
    readonly #logger = inject(Logger);
    readonly #router = inject(Router);
    readonly #window = inject(WINDOW);
    readonly #location = inject(Location);

    #currentStamp: number;

    constructor() {
        this.#currentStamp = this.stampHistoryIfNotStamped(); // Stamp on load/hard navigations.
    }

    init(): this {
        this.patchHistoryApi();
        this.initStampOnNavigation();

        return this;
    }

    initStampOnNavigation(): void {
        this.#router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => (this.#currentStamp = this.stampHistoryIfNotStamped()));
    }

    isBrowserBackNavigation(): boolean {
        if (!isNavigationTraversal()) {
            return false;
        }

        const incomingStamp = this.getStamp();

        if (incomingStamp === undefined) {
            this.#logger.error(`${LOG_TAG} Encountered unstamped history during traversal at ${this.#location.path(true)}`);

            return false;
        }

        const isBrowserBackNavigation = incomingStamp < this.#currentStamp;

        if (isBrowserBackNavigation) {
            this.#logger.info(`${LOG_TAG} ${BROWSER_BACK_NAVIGATION_DETECTED_MESSAGE}`);
        }

        return isBrowserBackNavigation;
    }

    private stampHistoryIfNotStamped(): number {
        const existingStamp = this.getStamp();

        if (existingStamp) {
            this.#logger.debug(`${LOG_TAG} Already stamped '${this.#location.path()}' at ${existingStamp}`);

            return existingStamp;
        }

        return this.stampHistory();
    }

    private stampHistory(): number {
        const stamp = Date.now();

        const stampedState = this.stampState(this.#location.getState(), stamp);

        this.#logger.debug(`${LOG_TAG} Stamping '${this.#location.path()}' at ${stamp}`);

        this.#location.replaceState(this.#location.path(true), undefined, stampedState);

        return stamp;
    }

    private getStamp(): number | undefined {
        const state = this.#location.getState();

        return this.isStampedState(state) ? state[TIMESTAMP_KEY] : undefined;
    }

    private isStampedState(state: unknown): state is StampedState {
        return isObject(state) && typeof (state as Record<string, unknown>)[TIMESTAMP_KEY] === 'number';
    }

    /**
     * This is an experimental monkey patch that forces history entries to always include a creation timestamp, enabling the determination
     * of every history traversal's direction (e.g. whether it's backwards or forwards). As a result, each history entry's state will always
     * be a defined object - and the API will ignore non-object types passed as state.
     *
     * However, since history state is global and persisted by the browser, it's good practice to use objects with namespaced keys and to
     * preserve existing state when replacing entries. This ensures you don't delete someone else's state - e.g. these timestamps (if this
     * patch weren't in place), or Angular's navigation IDs.
     *
     * Existing pushState and replaceState calls can be refactored instead of using a monkey patch (examples below). However, it may be
     * difficult to ensure this advice is always followed, and deviating from it could quietly introduce incorrect back/forward detection
     * and therefore reversed view transition directionality in specific cases.
     */

    /* eslint-disable no-param-reassign */
    private patchHistoryApi(): void {
        const _history = this.#window.history;

        const unpatchedPushState = _history.pushState;
        const unpatchedReplaceState = _history.replaceState;

        /** Timestamp new history entries */
        _history.pushState = (...args: Parameters<History['pushState']>) => {
            try {
                const stamp = Date.now();
                args[0] = this.stampState(args[0], stamp);
                this.#logger.debug(`${LOG_TAG} Adding timestamp ${stamp} to new history entry via patched pushState call`);
            } catch (error) {
                this.#window.console.error(`${LOG_TAG} Error stamping history entry: ${error}`);
            } finally {
                unpatchedPushState.apply(_history, args);
            }
        };

        /** Preserve original timestamps when replacing entries */
        _history.replaceState = (...args: Parameters<History['replaceState']>) => {
            try {
                const stamp = this.getStamp() ?? Date.now();
                args[0] = this.stampState(args[0], stamp);
                this.#logger.debug(`${LOG_TAG} Re-adding original timestamp ${stamp} via patched replaceState call`);
            } catch (error) {
                this.#window.console.error(`${LOG_TAG} Error stamping history entry: ${error}`);
            } finally {
                unpatchedReplaceState.apply(_history, args);
            }
        };
    }

    /** Stamps state if it's an object; avoids spreading arrays or strings into the new state. */
    private stampState(state: unknown, timestamp: number): StampedState {
        if (!isObject(state) || this.#window.Array.isArray(state) || isString(state)) {
            this.#logger.error(`${LOG_TAG} Non-object state encountered (${state}), creating new state with timestamp`);

            return { [TIMESTAMP_KEY]: timestamp };
        }

        return { ...state, [TIMESTAMP_KEY]: timestamp };
    }
}
